import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useLocale } from '../i18n/LocaleContext';
import { humanizeError } from '../util/humanizeError';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { chatListPalette } from '../theme/chatListPalette';
import { patchOwnAvatarShape } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AvatarEditorPanel } from '../components/AvatarEditorPanel';
import { UserAvatarPreviewModal, type UserAvatarPreviewTarget } from '../components/UserAvatarPreviewModal';
import {
  DEFAULT_AVATAR_SHAPE,
  normalizeAvatarShapeId,
  type AvatarShapeId,
} from '../avatar/shapeCatalog';

/**
 * Вкладка «Фото профиля».
 *
 * <p>Вся работа со снимком — в `AvatarEditorPanel`, общей с фото чата. Здесь остаётся то, чего
 * у чата нет: форма аватара живёт в профиле пользователя (отдельный PATCH) и карточка «как
 * меня видят другие».
 */
export function AvatarSettingsSection({ onClose }: { onClose: () => void }): React.ReactElement {
  const { t } = useLocale();
  const { resolvedVisualTheme } = useVisualTheme();
  // Та же палитра, что и на остальных вкладках настроек. Без неё подписи брали цвет по
  // умолчанию (чёрный на тёмной карточке модалки), и вкладка читалась как пустая.
  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);
  const { user, mergeUserAvatar } = useAuth();
  const [shape, setShape] = useState<AvatarShapeId>(
    normalizeAvatarShapeId(user?.avatarShape ?? DEFAULT_AVATAR_SHAPE),
  );
  const [err, setErr] = useState<string | null>(null);
  const [cardTarget, setCardTarget] = useState<UserAvatarPreviewTarget | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const userHasAvatar = user?.hasAvatar === true;
  const userAvatarRevision = user?.avatarRevision ?? 0;

  const onShapeChange = useCallback(
    (next: AvatarShapeId) => {
      setShape(next);
      setErr(null);
      void (async () => {
        try {
          await patchOwnAvatarShape(next);
          await mergeUserAvatar({
            hasAvatar: userHasAvatar,
            avatarRevision: userAvatarRevision,
            avatarShape: next,
          });
        } catch (e) {
          setErr(humanizeError(e, t('errorGeneric')));
        }
      })();
    },
    [mergeUserAvatar, t, userAvatarRevision, userHasAvatar],
  );

  const onUploaded = useCallback(
    (avatarRevision: number) => {
      void mergeUserAvatar({ hasAvatar: true, avatarRevision, avatarShape: shape });
      onClose();
    },
    [mergeUserAvatar, onClose, shape],
  );

  const onRemoved = useCallback(
    (avatarRevision: number) => {
      void mergeUserAvatar({ hasAvatar: false, avatarRevision, avatarShape: shape });
    },
    [mergeUserAvatar, shape],
  );

  if (!user) {
    return <Text style={[styles.muted, { color: pal.settingsFieldLabel }]}>{t('errorGeneric')}</Text>;
  }

  return (
    <View style={styles.sectionRoot}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollInner}
        keyboardShouldPersistTaps="handled">
        <Text style={[styles.sectionTitle, { color: pal.settingsSectionTitle }]}>
          {t('settingsAvatarTitle')}
        </Text>

        <AvatarEditorPanel
          target={{ kind: 'user', userId: user.userId }}
          displayName={user.displayName}
          hasAvatar={user.hasAvatar === true}
          revision={user.avatarRevision ?? 0}
          shapeId={shape}
          title={t('settingsAvatarTitle')}
          onShapeChange={onShapeChange}
          onUploaded={onUploaded}
          onRemoved={onRemoved}
        />

        <Pressable
          onPress={() => {
            setCardTarget({
              userId: user.userId,
              displayName: user.displayName,
              hasAvatar: user.hasAvatar,
              revision: user.avatarRevision ?? 0,
              shapeId: shape,
              // Своё фото: отсюда как раз и проверяют, что в полный экран уйдёт нужный кадр.
              canViewPhoto: true,
            });
            setCardOpen(true);
          }}
          accessibilityRole="button"
          accessibilityLabel={t('userCardTitle')}
          style={styles.linkBtn}>
          <Text style={[styles.linkText, { color: pal.modalPrimary }]}>{t('avatarPreviewCard')}</Text>
        </Pressable>

        {err ? <Text style={[styles.err, { color: pal.dmErr }]}>{err}</Text> : null}
        {Platform.OS === 'web' ? (
          <Text style={[styles.muted, { color: pal.settingsFieldLabel }]}>
            {t('settingsAvatarWebHint')}
          </Text>
        ) : null}
      </ScrollView>
      {/*
        Карточка — слой рядом со списком, а не <Modal>: сама секция уже живёт внутри модалки
        настроек, и вторая одновременная презентация подвешивает экран на iOS.
      */}
      <UserAvatarPreviewModal
        presentation="overlay"
        visible={cardOpen && cardTarget != null}
        onClose={() => {
          setCardOpen(false);
          setCardTarget(null);
        }}
        target={cardTarget}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  sectionRoot: { flexShrink: 1 },
  scroll: { maxHeight: 560 },
  scrollInner: { paddingBottom: 16, gap: 10 },
  sectionTitle: { fontSize: 16, fontWeight: '700', marginBottom: 4 },
  linkBtn: { minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  linkText: { fontSize: 14, fontWeight: '600' },
  muted: { fontSize: 12 },
  err: { fontSize: 13 },
});
